import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import UserManagement from './components/UserManagement';
import AppointmentManagement from './components/AppointmentManagement';
import PropertyOverview from './components/PropertyOverview';
import NotificationCenter from './components/NotificationCenter';
import Analytics from './components/Analytics';
import { Users, Calendar, Home, Bell, BarChart3 } from 'lucide-react';
import Icon from '../../components/AppIcon';


const UserAppointmentManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeAppointments: 0,
    totalProperties: 0,
    unreadNotifications: 0
  });
  const [loading, setLoading] = useState(true);

  // Fetch dashboard statistics
  const fetchStats = async () => {
    try {
      const [usersRes, appointmentsRes, propertiesRes, notificationsRes] = await Promise.all([
        supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true }),
        supabase?.from('appointments')?.select('id', { count: 'exact', head: true })?.eq('status', 'scheduled'),
        supabase?.from('properties')?.select('id', { count: 'exact', head: true }),
        supabase?.from('notifications')?.select('id', { count: 'exact', head: true })?.eq('is_read', false)
      ]);

      setStats({
        totalUsers: usersRes?.count || 0,
        activeAppointments: appointmentsRes?.count || 0,
        totalProperties: propertiesRes?.count || 0,
        unreadNotifications: notificationsRes?.count || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const tabConfig = [
    {
      id: 'users',
      label: 'Gestion des Utilisateurs',
      icon: Users,
      component: UserManagement
    },
    {
      id: 'appointments',
      label: 'Gestion des Rendez-vous',
      icon: Calendar,
      component: AppointmentManagement
    },
    {
      id: 'properties',
      label: 'Aperçu des Propriétés',
      icon: Home,
      component: PropertyOverview
    },
    {
      id: 'notifications',
      label: 'Centre de Notifications',
      icon: Bell,
      component: NotificationCenter
    },
    {
      id: 'analytics',
      label: 'Analyses & Rapports',
      icon: BarChart3,
      component: Analytics
    }
  ];

  const ActiveComponent = tabConfig?.find(tab => tab?.id === activeTab)?.component;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Interface d'Administration
              </h1>
              <p className="text-sm text-gray-600">
                Gestion complète des utilisateurs et des rendez-vous
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Users className="h-4 w-4" />
                  <span>{stats?.totalUsers} utilisateurs</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>{stats?.activeAppointments} RDV actifs</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Home className="h-4 w-4" />
                  <span>{stats?.totalProperties} propriétés</span>
                </div>
              </div>
              <div className="relative">
                <Bell className="h-6 w-6 text-gray-400 cursor-pointer hover:text-gray-600" />
                {stats?.unreadNotifications > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {stats?.unreadNotifications}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabConfig?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab?.id
                      ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="hidden sm:block">{tab?.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {ActiveComponent && (
          <ActiveComponent 
            onStatsUpdate={fetchStats}
            stats={stats}
          />
        )}
      </main>

      {/* Quick Stats Footer */}
      <div className="bg-white border-t mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-sm">
            <div>
              <p className="font-semibold text-gray-900">{stats?.totalUsers}</p>
              <p className="text-gray-500">Utilisateurs Total</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{stats?.activeAppointments}</p>
              <p className="text-gray-500">RDV Programmés</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{stats?.totalProperties}</p>
              <p className="text-gray-500">Propriétés Actives</p>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{stats?.unreadNotifications}</p>
              <p className="text-gray-500">Notifications</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAppointmentManagement;